import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useOutlet, useRevalidator } from 'react-router-dom';
import { useAuth } from '../providers/AuthContext';
import loadingGif from '../assets/Circles-menu-3.gif';

/**
 * ConfigGuard waits until GET network/local returns 200, then:
 * - if isConfigured: allow the app
 * - if not: redirect to /setup (or /admin/setup), allowing only setup routes
 * While the server is unreachable (pending), show a waiting UI — do not treat
 * proxy errors as "needs initial configuration".
 *
 * When the server recovers, revalidate route loaders so pages do not keep
 * mid-outage failures (e.g. Case Builder 502) as their loader data.
 */
const ALLOWED_WHEN_UNCONFIGURED = ['/setup', '/admin/setup', '/admin/setup/initial'];

function ConfigGuard() {
  const { girderInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();
  const revalidator = useRevalidator();
  const wasPendingRef = useRef(true);

  useEffect(() => {
    const isPending = girderInfo === 'pending';
    if (wasPendingRef.current && !isPending) {
      revalidator.revalidate();
    }
    wasPendingRef.current = isPending;
  }, [girderInfo, revalidator]);

  useEffect(() => {
    if (girderInfo === 'pending') {
      return;
    }
    const configured = girderInfo && typeof girderInfo === 'object' && girderInfo.isConfigured === true;
    if (configured) {
      return;
    }
    const path = location.pathname;
    const allowed = ALLOWED_WHEN_UNCONFIGURED.some((p) => path === p);
    if (allowed) {
      return;
    }
    if (path.startsWith('/admin')) {
      navigate('/admin/setup', { replace: true });
      return;
    }
    navigate('/setup', { replace: true });
  }, [girderInfo, location.pathname, navigate]);

  if (girderInfo === 'pending') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          background: '#fff',
          zIndex: 9999,
        }}
      >
        <img src={loadingGif} alt="" width={48} height={48} />
        <p style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>
          Waiting for the server…
        </p>
      </div>
    );
  }

  return outlet;
}

export default ConfigGuard;
